import axios, { isAxiosError } from 'axios';
import { RecognizePageRequest, RecognizePageResponse } from './_types';
import { env } from 'lib/env';
import { logInfo } from 'lib/util/errors';
import { getAbsolutePath } from 'lib/util/urls';

/**
 * Fetches recognition items from the server.
 *
 * @param {Object} params - The parameters for the request.
 * @param {number} params.page - The page number to fetch.
 * @param {number} params.count - The number of items per page.
 * @param {AbortSignal} params.signal - The signal to abort the request.
 * @returns {Promise<RecognizePageResponse>} A promise that resolves to the recognition page response.
 * @throws Will throw an error if the request fails for any reason other than being canceled.
 */
export const getRecognitionItems = async ({
  page,
  count,
  signal,
}: RecognizePageRequest): Promise<RecognizePageResponse> => {
  // Set query string
  const query = new URLSearchParams();
  const isServer = typeof window === 'undefined';
  const apiUrl = new URL(
    isServer ? env.SITECORE_API_HOST : getAbsolutePath('/api/employees/recognize')
  );
  // Set the path name
  if (isServer) {
    apiUrl.pathname = '/api/recognitionpage/list';
    query.set('sc_apikey', env.SITECORE_API_KEY);
    query.set('sc_site', env.JSS_APP_NAME);
  }
  apiUrl.search = query.toString();
  try {
    return await axios
      .post<RecognizePageResponse>(
        apiUrl.toString(),
        { page, count },
        {
          signal,
        }
      )
      .then((x) => {
        if (typeof x.data === 'string') {
          return JSON.parse(x.data);
        }
        return x.data;
      });
  } catch (e: unknown) {
    if (isAxiosError(e) && e.code === 'ERR_CANCELED') {
      logInfo({ source: 'getRecognitionItems', message: 'Request was canceled' });
      return { count: -1, children: [] };
    }
    throw e;
  }
};
